<template>
  <div class="mx-auto contentsWidth mt-4">
    <div class="">
      <p class="title mb-0">{{year}}年度 {{name}} - 申請報告</p>
      <hr class="title">
    </div>

    <!-- No.23 §16：タブは「取得完了後にまとめて」描画する。
         b-tabs はマウント後に追加されたタブを active 指定で開けないため（jest で検証済み＝
         __tests__/surveyTabActivation.spec.js）、アンケートタブが揃った状態で初回描画する必要がある。 -->
    <b-tabs
      v-if="loaded"
      class="gas-tab"
      nav-wrapper-class="gas-tab-wrap">
      <b-tab title="受領/差戻"
        title-link-class="px-5">
        <v-rep-control
          :status="status"
          :mailInfo="mailInfo"/>
      </b-tab>
      <b-tab>
        <template v-slot:title>
          <span>申請者情報</span>
          <img
            v-if="hasUserErr"
            class="errorMark"
            src="@/assets/svg/error.svg">
        </template>
        <v-app-userinfo
          :writable="true"
          :close_datetime="close_datetime"/>
      </b-tab>
      <b-tab
        v-for="(page, index) in pages" :key="index">
        <template v-slot:title>
          <span>{{page.name}}</span>
          <img
            v-if="hasPageErr[index]"
            class="errorMark"
            src="@/assets/svg/error.svg">
        </template>
        <v-app-form
          :writable="true"
          :pageIndex="index"
          :assets="assets"
          :downloadOpenFile="downloadOpenFile"
          :downloadFormFile="downloadFormFile"/>
      </b-tab>
      <!-- No.7 採択後手続き（編集可・報告の基本情報と同様）。採択者の報告フォームと同じ順＝アンケートの左側。 -->
      <b-tab>
        <template v-slot:title>
          <span>採択者手続き</span>
        </template>
        <v-app-adoption
          :value="adoptionData"
          @input="v => adoptionData = v"
          :templates="adoptionTemplates"
          :writable="true"
          :errors="{}"
          @select-file="onAdoptionSelectFile"
          @download-template="downloadAdoptionTemplate"
          @download-uploaded="downloadAdoptionUploaded"
          @download-other="downloadAdoptionOther"/>
      </b-tab>
      <!-- No.23：複数回アンケート。採択者の全回を回ごとに表示。
           §14 で閲覧専用（§B-18）から変更＝事務局は回答期間に関係なく編集できる。 -->
      <!-- §16：一覧のアンケートモード（?tab=survey）から来たときは、このタブを開いた状態で描画する。
           タブ index の指定では v-if でタブが後から増えるため間に合わない（末尾へ丸められる）。 -->
      <b-tab
        v-if="surveyRounds.length > 0"
        :active="openSurveyTab">
        <template v-slot:title>
          <span>アンケート</span>
        </template>
        <!-- 全回を1つの枠（他タブと同じ ContentsBox）に入れ、スクロールもタブ内で完結させる -->
        <perfect-scrollbar
          :options="scrollOption"
          class="py-2 px-2 ContentsBox">
          <div
            v-for="round in surveyRounds" :key="round.id"
            class="survey-round mb-4">
            <div class="survey-round-head px-3 py-2 mb-2">
              <span class="bold mr-2">{{ round.name }}</span>
              <span class="ml-2 text-muted small">{{ round.open_datetime }} 〜 {{ round.close_datetime }}</span>
              <!-- No.23 §20：入力できない回は理由を出す（判定はサーバ＝round.writable を素通し） -->
              <span v-if="!round.writable" class="ml-2 badge badge-secondary">{{ round.lock_reason }}</span>
            </div>
            <v-app-survey
              v-if="round.surveys.length > 0"
              :surveys="round.surveys"
              :value="surveyAnswersByRound[round.id] || {}"
              @input="v => onSurveyRoundInput(round.id, v)"
              :writable="round.writable"
              :framed="false"/>
            <p v-else class="text-muted px-3 py-2 mb-0">設問がありません。</p>
          </div>
        </perfect-scrollbar>
      </b-tab>
    </b-tabs>

    <div class="bottonArea mt-4 mb-3 flex flexCenter">
      <p
        v-show="hasEmpty"
        class="warning">
        未入力の必須項目があります。すべての必須項目に入力後、編集を行ってください。
      </p>
    </div>

    <div class="bottonArea mt-3 mb-3 flex flexCenter">
      <b-button
        v-show="hasPrevApp"
        @click="prevApp"
        class="btn btn-primary btn-lg mr-4 bold">
        前の申請
      </b-button>
      <b-button
        class="btn btn-lg mr-4 bold"
        to="/cms/reports/list">
        一覧に戻る
      </b-button>
      <b-button
        @click="save"
        variant="info"
        class="btn btn-primary btn-lg mr-4 bold">
        編集
      </b-button>
      <b-button
        v-show="hasNextApp"
        @click="nextApp"
        class="btn btn-primary btn-lg mr-4 bold">
        次の申請
      </b-button>
    </div>
  </div>
</template>

<script>
// import moment from 'moment';
import api from '@/modules/api';
import download from '@/modules/download';
import AppForm from '@/components/gas/parts/Form.vue';
import Userinfo from '@/components/parts/application/Userinfo.vue';
import RepControl from '@/components/cms/reports/Control.vue';
import Survey from '@/components/parts/report/Survey.vue';
import AdoptionProcedure from '@/components/parts/report/AdoptionProcedure.vue';
import { buildAnswerMap } from '@/modules/survey';

export default {
  name: 'cmsReportEdit',
  components: {
    'v-app-form': AppForm,
    'v-app-userinfo': Userinfo,
    'v-rep-control': RepControl,
    'v-app-survey': Survey,
    'v-app-adoption': AdoptionProcedure,
  },
  data() {
    return {
      base: {},
      reportId: null,
      repTypeId: null,
      year: null,
      name: '',
      status: null,
      hasUserErr: false,
      hasPageErr: [],
      assets: {},
      mailInfo: {},
      close_datetime: null,
      // No.23 §16：アンケートタブを開いた状態で表示するか（一覧のアンケートモードから来たとき）
      openSurveyTab: false,
      // No.23 §16：タブ一式の描画開始フラグ（取得完了までタブを描画しない）
      loaded: false,
      // No.23 アンケート（§14 で事務局も編集可）
      surveyRounds: [],
      surveyAnswersByRound: {},
      // No.23 §22：画面を開いた時点の回答（回ID → 送信形の JSON）。
      // 「変更した回だけ送る」ための比較基準にする。
      initialSurveyPayload: {},
      // アンケートタブの外枠スクロール（全回をまとめて1つのスクロールにする）
      scrollOption: {
        wheelPropagation: true,
        suppressScrollX: true,
      },
      // No.7 採択後手続き（編集可）
      adoptionData: this.initAdoptionData(null),
      adoptionTemplates: { donation: null, exemption: null },
      adoptionFiles: { donation: null, exemption: null },
    };
  },
  computed: {
    pages() {
      return this.$store.state.gasApplicationForm.pages;
    },
    filterdIdList() {
      return this.$store.state.cmsReportSearch.filterdIdList;
    },
    // No.23 §16-9：一覧がアンケートモードのとき filterdIdList は user_id を保持するため、
    // report_id 前提の「前の申請／次の申請」は無効にする。
    canMoveApp() {
      return this.$store.state.cmsReportSearch.search.mode !== 'survey';
    },
    filterdIdListIndex() {
      return this.filterdIdList.indexOf(Number(this.reportId));
    },
    hasNextApp() {
      if (!this.canMoveApp || this.filterdIdList.length === 0) {
        return false;
      }
      const newIndex = this.filterdIdListIndex + 1;
      return newIndex < this.filterdIdList.length;
    },
    hasPrevApp() {
      if (!this.canMoveApp || this.filterdIdList.length === 0) {
        return false;
      }
      const newIndex = this.filterdIdListIndex - 1;
      return newIndex >= 0;
    },
    hasEmpty() {
      // ユーザー情報
      const userInfo = this.$store.state.userInfo.inputData;
      const userInfoForm = this.$store.state.userInfoForm.form;
      const findUserResult = Object.keys(userInfo).find((key) => {
        if (!userInfoForm[key].is_required) {
          return false;
        }
        return userInfo[key] === '' || userInfo[key] === null;
      });

      // 入力フォーム
      const { inputs } = this.$store.state.gasApplicationInput;
      const formOptionData = this.$store.state.gasApplicationForm.optionDataList;
      const findFormResult = inputs.find((input) => {
        if (formOptionData[input.option_id] && !formOptionData[input.option_id].is_required) {
          return false;
        }
        const noSelect = input.field_answer_id === null;
        const notext = input.field_answer_text === '';
        const noFile = input.uploadedFileKey === '';
        return noSelect && notext && noFile;
      });
      return typeof findUserResult !== 'undefined' || typeof findFormResult !== 'undefined';
    },
    newStatus() {
      return this.$store.state.cmsReportControl.status;
    },
    blockMail() {
      return this.mailInfo.has_invalid_email_address;
    },
    isDeletedUser() {
      return Object.keys(this.mailInfo).length === 0;
    },
  },
  methods: {
    async fetchRepptype(repId) {
      const param = {
        repId,
      };
      const response = await api.send('/api/cms/reports/edit', param)
        .catch((err) => {
          console.error(err);
        });

      if (!response) {
        console.log('err');
        // await this.alert('申請のデータを取得できませんでした。');
        // this.$router.push({ path: 'cms/reports/list' });
        return;
      }
      const { base } = response.data;
      this.base = base;
      this.year = base.year;
      this.name = base.name;
      this.status = base.status;
      this.repTypeId = base.report_type_id;
      this.close_datetime = base.close_datetime;
      this.assets = response.data.assets;
      this.$store.commit('cmsReportControl/updateStatus', base.status);
      this.$store.commit('cmsReportControl/updateReason', base.reason);
      const {
        pages,
        userInfo,
        answers,
        userId,
      } = response.data;
      // ユーザーが削除されていたらからサーバーからnullが来るので空オブジェクトに置き換え
      this.mailInfo = response.data.mailInfo || {};
      this.$store.commit('gasApplicationForm/setDbData', pages);
      this.$store.commit('gasApplicationForm/setUserId', userId);
      this.$store.commit('gasApplicationInput/setDbData', { pages, answers });
      this.$store.commit('userInfo/setDbData', userInfo);
      // No.23 アンケートを取得
      await this.fetchSurveys(repId);
      // No.7 採択後手続き（閲覧専用）を取得
      await this.fetchAdoption(repId);
    },
    // 複数回アンケート：採択者の全回を回ごとに取得（§14 で事務局も編集可）
    async fetchSurveys(repId) {
      const res = await api.send('/api/cms/survey/answersByReport', { reportId: repId })
        .catch(() => false);
      if (!res) {
        return;
      }
      // §16：タブが描画される前にフラグを立てる（:active はタブ生成時に評価されるため）
      this.openSurveyTab = this.$route.query.tab === 'survey';
      this.surveyRounds = res.data.surveyRounds || [];
      const byRound = {};
      this.surveyRounds.forEach((round) => {
        byRound[round.id] = buildAnswerMap(round.surveys, round.answers);
      });
      this.surveyAnswersByRound = byRound;
      // No.23 §22：dirty 判定の基準を取る。送信形に整形した後の配列で比べる
      // （回答マップをそのまま比べるとキーの並びで誤検知しうるため）。
      const initial = {};
      this.surveyRounds.forEach((round) => {
        initial[round.id] = JSON.stringify(this.roundAnswersPayload(round));
      });
      this.initialSurveyPayload = initial;
    },
    // No.23 §14：回ごとの回答入力を反映（事務局も編集できる）
    onSurveyRoundInput(roundId, v) {
      this.$set(this.surveyAnswersByRound, roundId, v);
    },
    // No.23 §14：回1つ分の回答マップ → 送信用の answers 配列。
    // No.23 §22：dirty 判定にも使うため切り出してある（判定と送信で同じ整形を通す）。
    roundAnswersPayload(round) {
      const map = this.surveyAnswersByRound[round.id] || {};
      return (round.surveys || []).map((s) => {
        const a = map[s.id] || { answer_text: '', option_ids: [] };
        return {
          survey_id: s.id,
          answer_text: a.answer_text || '',
          option_ids: a.option_ids || [],
        };
      });
    },
    // No.23 §14：回ごとの回答マップ → 送信用 payload。
    // 採択者側（Form.vue）と違い state で絞らない＝事務局は回答期間に関係なく全回を編集できる。
    // No.23 §22：ただし「変更した回」だけを送る。保存は回ごとの DELETE→INSERT なので、
    // 触っていない回まで送ると、画面を開いてから保存するまでの間に採択者が回答した内容を
    // 開いた時点の内容（多くは空）で上書きしてしまう。
    toSurveyPayload() {
      return this.surveyRounds
        .map(round => ({
          survey_round_id: round.id,
          answer_version: round.answer_version,
          answers: this.roundAnswersPayload(round),
        }))
        .filter(r => JSON.stringify(r.answers) !== this.initialSurveyPayload[r.survey_round_id]);
    },
    // No.7：サーバーの採択後手続きデータ → コンポーネント value 形へ
    initAdoptionData(adoption) {
      const a = adoption || {};
      return {
        addressee: a.addressee || '',
        postalCode: a.postal_code || '',
        address: a.address || '',
        tel: a.tel || '',
        fax: a.fax || '',
        donation: { name: a.donation_name || '', submitted: !!a.donation_name, delete: false },
        exemption: { name: a.exemption_name || '', submitted: !!a.exemption_name, delete: false },
        // その他添付（複数）：既存DB＝otherFiles[{id,name}]／新規＝otherNew／削除id＝otherDeleteIds
        otherFiles: a.otherFiles || [],
        otherNew: [],
        otherDeleteIds: [],
      };
    },
    // No.7：当該報告の採択者手続き（編集可）＋①②テンプレ
    async fetchAdoption(repId) {
      const res = await api.send('/api/cms/reports/adoption', { report_id: repId })
        .catch(() => false);
      if (!res) {
        return;
      }
      this.adoptionData = this.initAdoptionData(res.data.adoption);
      this.adoptionTemplates = res.data.adoptionTemplates || { donation: null, exemption: null };
    },
    // No.7：value → 送信用（住所＋①②削除フラグ。実ファイルは別枠 adoptionFiles）
    toAdoptionPayload() {
      const d = this.adoptionData;
      return {
        addressee: d.addressee,
        postal_code: d.postalCode,
        address: d.address,
        tel: d.tel,
        fax: d.fax,
        donation: { delete: d.donation.delete },
        exemption: { delete: d.exemption.delete },
        otherDeleteIds: d.otherDeleteIds || [],
      };
    },
    // No.7：①②のファイル選択/削除（File を保持・null で選択解除）
    onAdoptionSelectFile({ key, file }) {
      this.adoptionFiles[key] = file;
    },
    // No.7：選択済みの①②ファイルを multipart に添付（backend: real_adoptionDonation/Exemption）
    attachAdoptionFiles(storeData) {
      const data = storeData;
      if (this.adoptionFiles.donation) {
        data.real_adoptionDonation = this.adoptionFiles.donation;
      }
      if (this.adoptionFiles.exemption) {
        data.real_adoptionExemption = this.adoptionFiles.exemption;
      }
      (this.adoptionData.otherNew || []).forEach((o, i) => {
        data[`real_adoptionOther[${i}]`] = o.file;
      });
    },
    // No.7：①②テンプレDL（fileType=4/5）
    async downloadAdoptionTemplate(fileType) {
      const param = { report_id: this.reportId, fileType };
      this.$store.dispatch('page/onWaiting');
      const response = await api.send('/api/cms/reports/adoption/download/template', param, true)
        .catch((err) => { console.error(err); });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('フォーマットのダウンロードに失敗しました。');
        return;
      }
      const key = fileType === 4 ? 'donation' : 'exemption';
      const tpl = this.adoptionTemplates[key];
      download.blob(response.data, (tpl && tpl.name) || 'format');
    },
    // No.7：管理者が提出済み①②をDL（key=donation/exemption）
    async downloadAdoptionUploaded(key) {
      const param = { report_id: this.reportId, fileKey: key };
      this.$store.dispatch('page/onWaiting');
      const response = await api.send('/api/cms/reports/adoption/download', param, true)
        .catch((err) => { console.error(err); });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, this.adoptionData[key].name || 'file');
    },
    // No.7 H-3：管理者がその他添付（既存DBファイル）を id 指定でDL
    async downloadAdoptionOther(id) {
      const target = (this.adoptionData.otherFiles || []).find(f => f.id === id);
      const param = { report_id: this.reportId, fileId: id };
      this.$store.dispatch('page/onWaiting');
      const response = await api.send('/api/cms/reports/adoption/download/other', param, true)
        .catch((err) => { console.error(err); });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, (target && target.name) || 'file');
    },
    async save() {
      let msg = '上記の内容で報告を編集登録します。';
      if (this.status !== Number(this.newStatus) && !this.blockMail && !this.isDeletedUser) {
        msg += '\nステータスが変更されているため、報告者にメールの送信と通知の登録が行われます。';
      } else if (this.status !== Number(this.newStatus) && !this.isDeletedUser) {
        msg += '\nステータスが変更されてますが、報告者へのメール送信がブロックされているため、通知の登録のみが行われます。';
      }

      msg += '\n編集登録してもよろしいですか？';
      if (!await this.confirm(msg)) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const storeData = {
        reptypeId: this.repTypeId,
        repId: this.reportId,
        userId: this.$store.state.gasApplicationForm.user_id,
        userInfo: JSON.stringify(this.$store.state.userInfo.inputData),
        inputs: JSON.stringify(this.$store.state.gasApplicationInput.inputs),
        control: JSON.stringify(this.$store.state.cmsReportControl),
        adoptionProcedure: JSON.stringify(this.toAdoptionPayload()),
        // No.23 §14：アンケート回答（表示している全回を送る＝送られた回だけが保存対象）
        surveyAnswers: JSON.stringify(this.toSurveyPayload()),
      };
      const { files } = this.$store.state.gasApplicationInput;
      Object.keys(files).forEach((key) => {
        storeData[key] = files[key];
      });
      this.attachAdoptionFiles(storeData);
      const response = await api.sendForm('/api/cms/reports/save', storeData)
        .catch(async (err) => {
          if (err.response.status === 422) {
            // No.23 §22：アンケートの版ズレ（他の画面で更新された）は入力の誤りではないため、
            // 他のエラー表示より先に案内して打ち切る。保存内容は破棄されるので再読込を促す。
            const conflicts = err.response.data.surveyConflicts;
            if (conflicts && conflicts.length > 0) {
              await this.alert(conflicts.map(c => c.message).join('\n'), false);
              return false;
            }
            await this.alert('入力内容に誤りがありました。修正してください。', false);
            this.$store.commit('gasApplicationInput/setErrMessages', err.response.data.formErrs);
            this.$store.commit('userInfo/setErrorMsg', err.response.data.userErrs);
            this.pageErrs = err.response.data.pageErrs;
            this.setErrorMarks(err.response.data.userErrs);
          } else {
            await this.alert(err.response.data.msg, false);
          }
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        return;
      }
      await this.alert('データを更新しました。');
      this.$store.dispatch('page/onLoading');
      this.$store.commit('gasApplicationForm/init');
      this.$store.commit('gasApplicationForm/setIsCms', true);
      this.hasPageErr = []; // タブのエラーマーク初期化
      const { repId } = this.$route.params;
      this.reportId = repId;
      await this.fetchRepptype(repId);
      this.$store.dispatch('page/offLoading');
    },
    async downloadOpenFile(assetId, assetName) {
      const param = {
        assetId,
      };
      this.$store.dispatch('page/onWaiting');
      const requireBlob = true;
      const response = await api.send('/api/cms/reports/download/file/form', param, requireBlob)
        .catch((err) => {
          console.error(err);
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, assetName);
    },
    async downloadFormFile(fieldId, optionId, fileName) {
      const param = {
        report_id: this.reportId,
        field_id: fieldId,
        option_id: optionId,
        answer_no: 1, // ファイルは当面１つ固定
      };
      param.user_id = this.$store.state.gasApplicationForm.user_id;
      const url = '/api/cms/reports/download/uploadedFile';

      this.$store.dispatch('page/onWaiting');
      const response = await api.send(url, param, true)
        .catch((err) => {
          console.error(err);
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, fileName);
    },
    async nextApp() {
      const newIndex = this.filterdIdListIndex + 1;
      this.$router.push({ path: `/cms/reports/edit/${this.filterdIdList[newIndex]}` });
      this.$store.dispatch('page/onLoading');
      this.reportId = this.filterdIdList[newIndex];
      await this.fetchRepptype(this.filterdIdList[newIndex]);
      this.$store.dispatch('page/offLoading');
    },
    async prevApp() {
      const newIndex = this.filterdIdListIndex - 1;
      this.$router.push({ path: `/cms/reports/edit/${this.filterdIdList[newIndex]}` });
      this.$store.dispatch('page/onLoading');
      this.reportId = this.filterdIdList[newIndex];
      await this.fetchRepptype(this.filterdIdList[newIndex]);
      this.$store.dispatch('page/offLoading');
    },
    setErrors(userErrs) {
      // ユーザー
      this.hasUserErr = Object.keys(userErrs).length > 0;
      // フォーム
      const { inputs } = this.$store.state.gasApplicationInput;
      Object.keys(this.pages).forEach((index) => {
        const { fields } = this.pages[index];
        const thisPageFieldIdList = Object.keys(fields).map((fieldKey) => {
          return fields[fieldKey].field_id;
        });
        let findFormErr = inputs.find((input) => {
          const searchedIndex = thisPageFieldIdList.indexOf(input.field_id);
          const hasErr = input.errMessages.length > 0;
          return searchedIndex !== -1 && hasErr;
        });
        // 例外的にページにエラーマークをつけたいとき
        if (this.pageErrs.includes(Number(index))) {
          findFormErr = true;
        }
        this.hasPageErr.splice(index, 1, findFormErr);
      });
    },
    setErrorMarks(userErrs) {
      // ユーザー
      this.hasUserErr = Object.keys(userErrs).length > 0;
      // フォーム
      const { inputs } = this.$store.state.gasApplicationInput;
      Object.keys(this.pages).forEach((index) => {
        const { fields } = this.pages[index];
        const thisPageFieldIdList = Object.keys(fields).map((fieldKey) => {
          return fields[fieldKey].field_id;
        });
        let findFormErr = inputs.find((input) => {
          const searchedIndex = thisPageFieldIdList.indexOf(input.field_id);
          const hasErr = input.errMessages.length > 0;
          return searchedIndex !== -1 && hasErr;
        });
        // 例外的にページにエラーマークをつけたいとき
        if (this.pageErrs.includes(Number(index))) {
          findFormErr = true;
        }
        this.hasPageErr.splice(index, 1, findFormErr);
      });
    },
  },
  // ロード画面
  async created() {
    this.$store.dispatch('page/onLoading');
    this.$store.commit('gasApplicationForm/init');
    this.$store.commit('gasApplicationForm/setIsCms', true);
    const { repId } = this.$route.params;
    this.reportId = repId;
    await this.fetchRepptype(repId);
    // No.23 §16：アンケートタブを含む全タブが揃ってから描画する（後付けでは初期タブを指定できない）
    this.loaded = true;
    this.$store.dispatch('page/offLoading');
  },
};
</script>

<style scoped>
  .bottonArea {
    width: 1100px;
  }
  .errorMark {
    position: absolute;
    top: -7px;
    left: 150px;
  }
  .warning {
    color: #dc3545;
  }
  .subject {
    border-bottom: .1rem solid #A0A9D0;
    margin: 3rem auto 0;
    width: 1080px;
  }

  .err-wrap>p{
    color: #dc3545;
  }
  /*
    No.23 複数回アンケート：回の見出し（採択者側 report/Form.vue と同一）。
    外枠（ContentsBox）はタブ直下に1つだけ置き、回ごとの見出しはその中の区切り帯にする。
    回ごとに枠を持たせると、2回目以降がタブから切り離された別の箱に見えてしまうため。
  */
  .survey-round-head {
    background-color: #F3F3F3;
    border: solid 1px #CCC;
  }
</style>
